/**
 * INSOLIP Landing Page Content
 * Extracted from landingPageContext JSON
 */

export const content = {
  // Product Info
  productName: "INSOLIP",
  tagline: "Secure Today, Lead Tomorrow.",
  heroSubtitle: "Insolip's IMS evaluates, assesses, and protects intellectual property — transforming innovation into defensible, long-term strategic assets.",
  heroTiles: [
    {
      heading: "WHAT INSOLIP IS",
      intro: "Innovation-to-IP Translation System Providing the Bridge Between Innovation and Value.",
      details: [
        "Most innovations fail not because they lack value, but because they are poorly documented, inconsistently evaluated, and never prepared for intellectual property protection.",
      ],
    },
    {
      heading: "HOW IT WORKS",
      details: [
        "Capture Innovation Details — Submit invention, research, or product information through a guided evaluation framework designed to identify protectable IP early.",
        "Assess Protectability & Risks — Analyze potential filing risks before investing in costly applications.",
        "Generate Decision-Ready Reports — Receive structured evaluation insights that support faster IP, R&D, and investment decisions.",
        "Produce Filing-Ready Documentation — Organize key invention details into patent-preparation documentation aligned with application requirements.",
      ],
    },
    {
      heading: "WHY THIS MATTERS",
      details: [
        "Without structure, innovation is lost.",
        "Without evaluation, value is unclear.",
        "Without documentation, IP protection fails.",
        "Save time on IP preparation.",
        "Reduce legal costs early.",
        "Improve quality of IP filings.",
        "Strengthen your innovation pipeline.",
        "Increase chances of funding.",
        "Provide structured documentation.",
        "Insolip helps innovators, companies, startups, universities, and research institutions turn raw ideas into structured, IP-ready assets that can be protected, funded, and commercialized.",
      ],
    },
  ],
  shortDescription: "A cloud-native Innovation Management System (IMS) designed to evaluate, assess, and protect intellectual property.",
  longDescription: "INSOLIP is a cloud-native Innovation Management System designed to evaluate, assess, and protect intellectual property. Streamline your innovation workflow from submission to assessment and protection. The platform supports innovators, reviewers, and institutions with role-based access, innovation registry, document uploads, bulk import for institutions, structured assessment, and billing governance—built on a microservices architecture for reliability and scalability.",

  // Mission & Vision
  mission: "To democratize innovation management by providing institutions, researchers, and innovators with enterprise-grade tools to capture, evaluate, and protect intellectual property efficiently and transparently.",
  vision: "A world where every innovation has a clear path from idea to impact, supported by transparent, scalable, and secure technology.",

  // Values
  values: [
    { name: "Transparency", description: "Structured evaluation and role-based access to support consistent, auditable processes.", icon: "Eye" },
    { name: "Security", description: "Strong authentication, encryption for sensitive data, and secure document storage.", icon: "Shield" },
    { name: "Scalability", description: "Cloud-native, microservices-based design for growth and performance.", icon: "TrendingUp" },
  ],

  // Target clients (for section under hero)
  targetClients: [
    { id: 'researchers', title: 'Researchers', description: 'Submit and track your innovations, upload supporting documents, and receive structured assessments with clear IP potential and filing recommendations.', icon: 'FlaskConical' },
    { id: 'innovators', title: 'Innovators', description: 'Bring ideas to life with a clear path from submission to assessment and protection. Upload documents, track status, and get transparent feedback.', icon: 'Lightbulb' },
    { id: 'industries', title: 'Industries', description: 'Scale innovation management across your organization with structured evaluation, IP protection guidance, and workflows built for industry.', icon: 'Factory' },
    { id: 'smes', title: 'SMEs', description: 'Capture and protect your intellectual property with a simple workflow, from idea to assessment to deployment—built for small and medium enterprises.', icon: 'Building2' },
    { id: 'institutions', title: 'Institutions', description: 'Manage innovation at scale with bulk import, institutional dashboards, and subscription tiers designed for universities and schools.', icon: 'GraduationCap' },
  ],

  // Why us – key platform benefits (for Why Us section)
  whyUs: [
    { title: 'End-to-end workflow', description: 'From idea submission through evaluation to IP filing—one platform, clear stages, and full visibility for innovators and institutions.', icon: 'Workflow' },
    { title: 'Structured assessment', description: 'Consistent evaluation criteria, expert reviewer assignment, and clear recommendations so every innovation gets a fair, transparent assessment.', icon: 'ClipboardCheck' },
    { title: 'Secure & scalable', description: 'Enterprise-grade security, role-based access, and cloud-native architecture so you can grow without compromising trust or performance.', icon: 'Shield' },
    { title: 'Built for institutions', description: 'Bulk import, dashboards, invoicing, and subscription tiers tailored for universities, research centres, and schools.', icon: 'Building2' },
  ],

  // Stats (no "Value Commercialized")
  stats: [
    { label: "Innovations Managed", value: 1000, suffix: "+", icon: "Lightbulb" },
    { label: "Time Saved", value: 80, suffix: "%", icon: "Clock" },
    { label: "Institutions", value: 50, suffix: "+", icon: "Building2" },
    { label: "IP Assets Protected", value: 100, suffix: "%", icon: "CheckCircle2" },
  ],

  // Target Audiences
  audiences: [
    { role: "Innovator", icon: "Lightbulb", painPoints: ["Need to submit and track innovations easily", "Managing documents and progress across stages", "Understanding assessment outcomes and IP potential"], benefit: "Submit and manage innovations with an intuitive interface, upload documents, track status, and receive structured assessment and IP recommendations." },
    { role: "Reviewer", icon: "FileSearch", painPoints: ["Evaluating many submissions efficiently", "Consistent criteria and transparency", "Managing assignment load and expertise matching"], benefit: "Streamlined review process with role-based access, structured evaluation criteria, and clear workflow for assessment and IP recommendations." },
    { role: "Institution / Institutional Admin", icon: "Building2", painPoints: ["Managing many innovations and submitters", "Bulk submission and reporting", "Billing and fiscal governance for assessment fees"], benefit: "Bulk import (Excel/CSV), institutional dashboards, order and invoice management, and subscription tiers (e.g. standard, premium)." },
    { role: "Administrator", icon: "Settings", painPoints: ["System oversight, user management", "Analytics and reporting across submissions and assessments"], benefit: "Dashboard with overview of submissions, pending/completed assessments, IP filed, filters by institution/category/reviewer, and user management." },
  ],

  // Features
  features: [
    { category: "Innovation Registry", name: "Innovation Submission & Registry", description: "Submit and manage innovations with ease. Create drafts, add title, abstract, stage (idea to demo), keywords, and technology area; track status from draft to submitted.", icon: "FileText" },
    { category: "Innovation Registry", name: "Document Uploads", description: "Upload supporting documents (technical specs, business plan, lab results, CAD, other) with validation and secure storage; optional envelope encryption for sensitive files.", icon: "Upload" },
    { category: "Innovation Registry", name: "Bulk Import", description: "Institutions can bulk upload innovations via Excel or CSV (with optional ZIP of documents); batch processing with validation reports and error handling.", icon: "Database" },
    { category: "Assessment", name: "Structured Evaluation", description: "Review interface with assessment decision (has potential / no potential for IP), IP protection type recommendations, filing instructions, and optional KTH-style prompts.", icon: "ClipboardCheck" },
    { category: "Assessment", name: "Reviewer Assignment & Workload", description: "Reviewer profiles with expertise, max/current assignments, and bio; assignment and workload visibility (assessment service planned for future).", icon: "Users" },
    { category: "Platform", name: "Secure & Scalable Infrastructure", description: "Microservices architecture, enterprise-grade security (Helmet, rate limiting, bcrypt/Argon2), optional mTLS and Keycloak/OIDC; cloud-native design for reliability and scale.", icon: "Server" },
  ],

  // Services — Card 03 is "Deployment" (not Commercialization)
  services: [
    { title: "Innovation Management", description: "End-to-end workflow for capturing, tracking, and managing innovations from submission through assessment with role-based access and comprehensive dashboards.", icon: "Workflow", image: "innovation-workflow" },
    { title: "IP Assessment & Protection", description: "Structured evaluation process with expert reviewers, IP protection type recommendations, and clear filing instructions to maximize your intellectual property value.", icon: "Shield", image: "ip-assessment" },
    { title: "Deployment", description: "Strategic frameworks to bring validated IP assets into active protection and deployment — ensuring legal defensibility, structural readiness, and long-term innovation leadership.", icon: "GraduationCap", image: "training-capacity" },
  ],

  // Key expertise (replaces team – skills/headlines only, no names or photos)
  expertise: [
    { title: "Innovation & IP Strategy", description: "Strategic guidance on capturing, evaluating, and protecting innovations and intellectual property.", icon: "Lightbulb" },
    { title: "Platform & Product Development", description: "Building and maintaining secure, scalable innovation management and assessment tools.", icon: "Code2" },
    { title: "Training & Capacity Building", description: "Design and delivery of training and capacity-building programmes for institutions and users.", icon: "GraduationCap" },
    { title: "Governance & Operations", description: "Operational excellence, compliance, and governance to keep the platform reliable and transparent.", icon: "Shield" },
  ],

  // Partners
  partners: [
    { name: "University of Nairobi", description: "Premium partner institution utilizing INSOLIP for comprehensive innovation and IP management.", tier: "Premium", logo: "university-nairobi" },
    { name: "Kenyatta University", description: "Standard partner institution streamlining their innovation evaluation and protection processes.", tier: "Standard", logo: "kenyatta-university" },
  ],

  // News
  news: [
    { category: "Press Release", title: "INSOLIP Launches Cloud-Native Innovation Management Platform", date: "2025-01-15", author: "INSOLIP Team", excerpt: "We're excited to announce the official launch of INSOLIP, a comprehensive innovation management system designed to streamline IP evaluation and protection for institutions worldwide.", featured: true, image: "launch-announcement" },
    { category: "Article", title: "The Future of IP Management: Why Cloud-Native Matters", date: "2025-02-01", author: "INSOLIP Team", excerpt: "Explore how microservices architecture and cloud-native design are transforming the way institutions manage intellectual property and innovation workflows.", featured: false, image: "cloud-technology" },
    { category: "Article", title: "Streamlining University Innovation: A Case Study", date: "2025-02-10", author: "INSOLIP Team", excerpt: "Learn how leading universities are using INSOLIP to reduce evaluation time by 80% while improving transparency and collaboration between innovators and reviewers.", featured: false, image: "university-innovation" },
  ],

  // Contact
  contact: {
    email: "info@insoliptechnologies.com",
    phone: "+254 700 422 973",
    address: "Nairobi, Kenya",
    socials: { twitter: "#", linkedin: "#", github: "#", youtube: "#" },
  },

  cta: { primary: "Get Started", secondary: "Learn More", register: "Register Your Business" },
  colors: { primary: "gold", secondary: "royal", dark: "var(--navy)" },

  navigation: [
    { label: "Home", href: "#hero" },
    { label: "Who We Serve", href: "#target-clients" },
    { label: "Why Us", href: "#why-us" },
    { label: "Stats", href: "#stats" },
    { label: "About", href: "#about" },
    { label: "Features", href: "#features" },
    { label: "Services", href: "#services" },
    { label: "Key Expertise", href: "#team" },
    { label: "Partners", href: "#partners" },
    { label: "News", href: "#news" },
    { label: "Contact", href: "#contact" },
  ],

  footerLinks: {
    navigation: [
      { label: "Home", href: "#hero" },
      { label: "About", href: "#about" },
      { label: "Features", href: "#features" },
      { label: "Contact", href: "#contact" },
    ],
    services: [
      { label: "Innovation Management", href: "#services" },
      { label: "IP Assessment", href: "#services" },
      { label: "Deployment", href: "#services" },
      { label: "Support", href: "#contact" },
    ],
    connect: [
      { label: "Contact Us", href: "#contact" },
      { label: "News & Updates", href: "#news" },
      { label: "Partners", href: "#partners" },
    ],
    legal: [
      { label: "Privacy Policy", href: "#" },
      { label: "Terms of Service", href: "#" },
      { label: "Cookie Policy", href: "#" },
    ],
  },
};
